/* eslint-disable react/no-unescaped-entities */
import PillarPage from "./PillarPage";

const HERO = "https://images.unsplash.com/photo-1512453979798-5ea266f8880c?w=1600";

const TOC = [
  { id: "eligibility", label: "Eligibility & thresholds" },
  { id: "how-to-apply", label: "How to apply" },
  { id: "documents", label: "Documents you'll need" },
  { id: "costs", label: "Real costs 2026" },
  { id: "timeline", label: "Timeline & processing" },
  { id: "benefits", label: "Benefits of a Golden Visa" },
  { id: "pitfalls", label: "Common pitfalls" },
];

const SECTIONS = [
  {
    id: "eligibility",
    heading: "Who qualifies for the Golden Visa via property in 2026",
    body: (
      <>
        <p>
          The UAE Golden Visa is a 10-year renewable residency granted to qualifying investors, entrepreneurs,
          specialised talents and property owners. The property route is the fastest and most predictable
          for international buyers because the criteria are strictly financial:
        </p>
        <ul>
          <li><b>AED 2,000,000 minimum</b> — the purchased property (or the loan-adjusted equity, see below) must be worth at least AED 2M.</li>
          <li><b>Freehold-designated area</b> — the unit must be in a designated freehold zone (Downtown Dubai, Marina, Palm Jumeirah, Business Bay, JVC, Dubai Hills and every other zone Point Penta lists).</li>
          <li><b>Off-plan is now eligible</b> — since April 2022 you can qualify with an off-plan property registered under Oqood, provided the AED 2M threshold is met and 50%+ is paid.</li>
          <li><b>Ready-to-move property</b> — full ownership title deed at AED 2M+ qualifies immediately.</li>
          <li><b>Mortgaged units</b> — allowed if buyer has paid at least AED 2M in equity (down payment + principal repaid).</li>
        </ul>
        <p>
          Multiple properties can be combined to reach AED 2M, provided the total across all title deeds
          exceeds the threshold and every unit is held in the applicant's name.
        </p>
      </>
    ),
  },
  {
    id: "how-to-apply",
    heading: "Step-by-step application flow",
    body: (
      <ol>
        <li><b>Complete the property purchase.</b> Sign Form F, transfer at DLD, and receive title deed (or Oqood for off-plan).</li>
        <li><b>Obtain the Property Verification Certificate</b> from Dubai Land Department confirming the AED 2M valuation.</li>
        <li><b>Apply through Amer Center / ICP portal.</b> Fee: AED 3,800 for main applicant; add family sponsorship at AED 2,800 per dependant.</li>
        <li><b>Complete medical fitness test</b> and biometric capture (usually same day).</li>
        <li><b>Receive Golden Visa</b> within 15–30 working days, e-linked to your Emirates ID.</li>
        <li><b>Enrol family</b> — spouse, unmarried daughters (any age), sons up to 25, and parents (with additional insurance).</li>
      </ol>
    ),
  },
  {
    id: "documents",
    heading: "Documents required",
    body: (
      <ul>
        <li>Passport copy (6+ months validity) — all applicants and dependants</li>
        <li>Recent passport-size photograph (white background)</li>
        <li>Title deed OR Oqood + payment receipts totalling ≥ AED 2M</li>
        <li>Property valuation certificate from DLD (issued in AED)</li>
        <li>Emirates ID (if you already hold one)</li>
        <li>Medical fitness certificate</li>
        <li>Marriage certificate (attested) — for spouse sponsorship</li>
        <li>Children's birth certificates (attested) — for dependant sponsorship</li>
        <li>Bank statement (last 3 months) — occasionally requested</li>
      </ul>
    ),
  },
  {
    id: "costs",
    heading: "Real cost breakdown — 2026 rates",
    body: (
      <>
        <p>Beyond the AED 2M property outlay, budget the following authority fees:</p>
        <table>
          <thead>
            <tr><th>Item</th><th>Fee (AED)</th></tr>
          </thead>
          <tbody>
            <tr><td>Golden Visa (10 yrs) — main applicant</td><td>3,800</td></tr>
            <tr><td>Medical fitness test</td><td>320</td></tr>
            <tr><td>Emirates ID (10 yrs)</td><td>1,150</td></tr>
            <tr><td>Property valuation certificate (DLD)</td><td>4,020</td></tr>
            <tr><td>Spouse sponsorship</td><td>2,800</td></tr>
            <tr><td>Child sponsorship (per child)</td><td>2,800</td></tr>
            <tr><td>Parent sponsorship (medical insurance included)</td><td>~5,000</td></tr>
          </tbody>
        </table>
        <p>
          Point Penta bundles the visa processing into every AED 2M+ purchase we handle — you never see a
          hidden fee.
        </p>
      </>
    ),
  },
  {
    id: "timeline",
    heading: "Realistic timeline",
    body: (
      <ul>
        <li><b>Cash purchase → visa in hand: ~4 weeks.</b> Two weeks to close the property + two weeks for visa processing.</li>
        <li><b>Off-plan purchase → visa in hand: 6–8 weeks.</b> Oqood + payment proof adds two weeks for DLD verification.</li>
        <li><b>Mortgaged purchase → visa in hand: 8–10 weeks.</b> Bank approval + property valuation adds four weeks.</li>
      </ul>
    ),
  },
  {
    id: "benefits",
    heading: "Why the Golden Visa is worth it",
    body: (
      <ul>
        <li><b>10-year renewable residency</b> — no sponsor required.</li>
        <li><b>Unlimited entry/exit</b> and no minimum stay requirement (unlike investor visa's 6-month rule).</li>
        <li><b>Sponsor family without employer</b> — spouse, unmarried daughters, sons ≤25, parents.</li>
        <li><b>UAE bank accounts unlocked</b> — including premium tiers.</li>
        <li><b>Zero personal income tax</b> continues to apply.</li>
        <li><b>Path to future citizenship</b> for exceptional talents (case-by-case).</li>
        <li><b>Business setup ease</b> — sponsor mainland companies without local partner.</li>
      </ul>
    ),
  },
  {
    id: "pitfalls",
    heading: "Pitfalls Point Penta helps you avoid",
    body: (
      <ul>
        <li><b>Buying below AED 2M assuming market gains will close the gap</b> — visa is granted on the DLD valuation at time of application, not on future appreciation.</li>
        <li><b>Holding units in a company name</b> — Golden Visa requires personal ownership. Structure this from day one.</li>
        <li><b>Combining spouse-owned units to reach AED 2M</b> — only the applicant's name counts; joint ownership needs explicit note.</li>
        <li><b>Missing the property valuation step</b> — the DLD-issued valuation certificate is mandatory; a bank valuation does not substitute.</li>
        <li><b>Off-plan buyers assuming Oqood is enough</b> — you must have paid ≥50% of the price for the visa to trigger.</li>
      </ul>
    ),
  },
];

const FAQS = [
  { q: "What is the minimum property value for a UAE Golden Visa in 2026?", a: "AED 2,000,000. Purchased property, off-plan (with ≥50% paid), or a combination of properties totalling AED 2M+ in the applicant's name qualifies for the 10-year Golden Visa." },
  { q: "Can I get a Golden Visa with an off-plan property in Dubai?", a: "Yes. Since April 2022, off-plan qualifies provided the value is AED 2M+ and at least 50% has been paid. The Oqood certificate and payment receipts serve as proof." },
  { q: "How long does the UAE Golden Visa process take?", a: "Cash purchases → visa issued in about 4 weeks. Off-plan → 6–8 weeks. Mortgaged → 8–10 weeks. Point Penta handles all paperwork end-to-end." },
  { q: "Can I include my family in the Golden Visa?", a: "Yes. Your spouse, unmarried daughters (any age), sons up to 25, and parents can be sponsored. Fees are AED 2,800 per dependant plus medical insurance for parents." },
  { q: "Does a mortgaged property qualify for a Golden Visa?", a: "Yes, if the equity you've paid in (down payment + principal repaid) totals at least AED 2 million. The remaining mortgage does not disqualify you." },
  { q: "Is there any minimum stay requirement for the Golden Visa?", a: "No. Unlike the standard investor visa, the Golden Visa has no minimum stay requirement — you can live abroad and keep your residency active for the full 10 years." },
  { q: "Do I pay tax in UAE after getting the Golden Visa?", a: "No personal income tax, no capital gains tax, no rental income tax. UAE remains one of the world's most tax-efficient residencies. Corporate tax (9%) only applies to businesses earning over AED 375,000 annually." },
  { q: "Can I lose my Golden Visa if I sell the property?", a: "Yes — if you drop below the AED 2M threshold. To preserve the visa, replace the sold unit with an equivalent property within 6 months, or top up with another qualifying purchase." },
];

export default function GoldenVisaPillar() {
  return (
    <PillarPage
      slug="golden-visa-dubai"
      category="Residency & investment"
      title="UAE Golden Visa via Property — 2026 Complete Guide"
      hero={HERO}
      updated="Feb 2026"
      intro="The AED 2 million property route is the fastest, most predictable path to a 10-year UAE Golden Visa in 2026. This guide walks through eligibility, real fees, exact timelines and the pitfalls Point Penta clients pay us to help them avoid."
      toc={TOC}
      sections={SECTIONS}
      faqs={FAQS}
    />
  );
}
